/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { BaseQueryFn, FetchArgs, FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { Mutex } from "async-mutex";
import { CognitoJwtVerifier } from "aws-jwt-verify";
import { useEffect, useState } from "react";
import { CognitoIdTokenPayload } from "aws-jwt-verify/jwt-model";

export const authCallbackUri = window.location.origin + ( process.env.REACT_APP_PATH_PREFIX ?? "/" );

export type TokenRequest = {
    grant_type: "authorization_code",
    client_id: string,
    code: string,
    redirect_uri: string,
};

export type TokenResponse = {
    access_token: string,
    id_token: string,
    refresh_token?: string,
    token_type: string,
    expires_in: number,
};

type RefreshRequest = {
    grant_type: "refresh_token",
    client_id: string,
    refresh_token: string,
};

export async function fetchToken( code: string )
{
    const tokenRequest: TokenRequest = {
        client_id: process.env.REACT_APP_COGNITO_CLIENT_ID!,
        grant_type: "authorization_code",
        code: code,
        redirect_uri: authCallbackUri,
    };

    return fetch( process.env.REACT_APP_OAUTH_DOMAIN + "/oauth2/token", {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Accept": "application/json",
        },
        redirect: "follow",
        body: Object.entries( tokenRequest ).map( ( [ key, value ] ) => encodeURIComponent( key ) + "=" + encodeURIComponent( value ) ).join( "&" ),
    } );
}

export async function refreshToken()
{
    const retrievedRefreshToken = getRefreshToken();
    if ( retrievedRefreshToken === undefined )
    {
        return Promise.reject( new Error( "Refresh token not found." ) );
    }

    const refreshRequest: RefreshRequest = {
        grant_type: "refresh_token",
        client_id: process.env.REACT_APP_COGNITO_CLIENT_ID!,
        refresh_token: retrievedRefreshToken,
    };

    return fetch( process.env.REACT_APP_OAUTH_DOMAIN + "/oauth2/token", {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            "Accept": "application/json",
        },
        redirect: "follow",
        body: Object.entries( refreshRequest ).map( ( [ key, value ] ) => encodeURIComponent( key ) + "=" + encodeURIComponent( value ) ).join( "&" ),
    } );
}

const AUTH_KEY = "auth";

export function getAccessToken(): string | null
{
    let accessToken: string | null = null;
    const authItem = sessionStorage.getItem( AUTH_KEY );

    if ( authItem !== null )
    {
        const tokenResponse: TokenResponse = JSON.parse( authItem );
        accessToken = tokenResponse.access_token;
    }

    return accessToken;
}

export function getIdToken(): string | null
{
    let idToken: string | null = null;
    const authItem = sessionStorage.getItem( AUTH_KEY );

    if ( authItem !== null )
    {
        const tokenResponse: TokenResponse = JSON.parse( authItem );
        idToken = tokenResponse.id_token;
    }

    return idToken;
}

function getRefreshToken(): string | undefined
{
    let retrievedRefreshToken: string | undefined = undefined;
    const authItem = sessionStorage.getItem( AUTH_KEY );

    if ( authItem !== null )
    {
        const tokenResponse: TokenResponse = JSON.parse( authItem );
        retrievedRefreshToken = tokenResponse.refresh_token;
    }

    return retrievedRefreshToken;
}

export const idVerifier = CognitoJwtVerifier.create( {
    userPoolId: process.env.REACT_APP_COGNITO_POOL_ID!,
    tokenUse: "id",
    clientId: process.env.REACT_APP_COGNITO_CLIENT_ID!,
} );

export function storeTokenResponse( response: TokenResponse )
{
    sessionStorage.setItem( AUTH_KEY, JSON.stringify( response ) );
}

function updateToken( reponse: TokenResponse )
{
    const refreshTokenToUpdate = getRefreshToken();
    storeTokenResponse( {
        ...reponse,
        refresh_token: refreshTokenToUpdate!,
    } );
}

function clearToken()
{
    sessionStorage.removeItem( AUTH_KEY );
}

// Want asingle mutext for all APIs locked by this token
const mutex = new Mutex();

export function createBaseQueryWithReauth( baseQuery: BaseQueryFn<
string | FetchArgs,
unknown,
FetchBaseQueryError
> )
{

    const baseQueryWithReauth: BaseQueryFn<
    string | FetchArgs,
    unknown,
    FetchBaseQueryError
    > = async ( args, api, extraOptions ) =>
    {

        // wait until the mutex is available without locking it
        await mutex.waitForUnlock();

        const result = await baseQuery( args, api, extraOptions );

        // Uncomment the following block to manually insert an authentication timeout for testing
        /*const tmp = Math.random();
    if (tmp < 0.5) {
      // @ts-ignore
      result = {...result, "error":{...result.error, "status":401}};
    }*/
        if ( result.error && result.error.status === 401 )
        {
            if ( getAccessToken() === null )
            {
                console.error( "No token." );
                return result;
            }

            console.error( "auth error" );
            if ( !mutex.isLocked() )
            {
                const release = await mutex.acquire();
                try
                {
                    const promise = refreshToken();
                    return promise.then( response =>
                    {
                        if ( response.ok )
                        {
                            return response.json();
                        }
                        return Promise.reject( new Error( "Error refreshing token" ) );
                    } ).then( json =>
                    {
                        /* Note: because this logic is inside of a "then()" method, we need to return
                Promise types (so the outer method can actually use the return value from here */
                        updateToken( json );
                        try
                        {
                            return baseQuery( args, api, extraOptions );
                        }
                        catch ( err )
                        {
                            return Promise.reject( err );
                        }
                    } ).catch( error =>
                    {
                        console.error( error, "clearing and restarting auth" );
                        clearToken();
                        window.location.reload();
                        return Promise.reject( new Error( "Error refreshing token - reloading" ) );
                    } );

                }
                finally
                {
                    release();
                }
            }
            else
            {
                await mutex.waitForUnlock();
                return baseQuery( args, api, extraOptions );
            }
        }
        return result;
    };
    return baseQueryWithReauth;
}

export const useIdentity = ( userViewOverride: boolean ) =>
{
    const [ identity, setIdentity ] = useState<CognitoIdTokenPayload | null>( null );

    useEffect( () =>
    {
        async function verifyId()
        {
            const idToken = getIdToken();
            if ( idToken !== null )
            {
                try
                {
                    const payload = await idVerifier.verify( idToken );
                    setIdentity( payload );
                }
                catch ( err )
                {
                    setIdentity( null );
                    console.error( "Request failed", err );
                }
            }
        }

        verifyId().catch( ( err ) => console.error( "Request failed", err ) );
    }, [] );

    const isAdmin =
        userViewOverride
            ? false
            : ( identity && String( identity["custom:id"] ).replaceAll( / /g, "" ) === "0" ) ?? false;

    return { identity, isAdmin };
};

export default useIdentity;
